import { useEntities } from 'utils/hooks/api';
import {
    DATA_SOURCES,
    INSTAGRAM_COUNTRIES,
    TIKTOK_COUNTRIES,
    hasInstagramBrandCat2Coverage,
    isCovered,
    isInstagramBrandCat2Covered,
} from '@/config/constants';
import {
    DataSourceBreadCrumbs,
    DataSourceIndustriesOnlyBreadCrumbs,
} from 'components';
import {
    FLAVORS_METADATA_METRIC,
    FLAVORS_SORT_OPTIONS,
} from 'components/FlavorFilters/utils';
import {
    GOOGLE_METADATA_BY_KEY,
    GOOGLE_SORT_OPTIONS,
    INSTAGRAM_METADATA_BY_KEY,
    INSTAGRAM_SORT_OPTIONS,
} from 'utils/metrics';
import {
    TikTokNameBodyCol,
    TikTokNameHashtagListCol,
} from 'components/TikTokTable';
import { useEffect } from 'react';
import {
    generatePath,
    useLocation,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import {
    useAbortController,
    useCurrentCountry,
    useInstagramData,
    useInstagramWeeks,
} from 'utils/hooks';
import DataSourceTabsByCoverage from 'components/DataSourceTabsByCoverage';
import Drawer from 'components/Drawer';
import ExploreDataSourceTable from 'components/DataVis/DataTable/ExploreDataSourceTable';
import ExploreInstagramHashtagsTable from 'components/DataVis/DataTable/ExploreInstagramHashtagsTable';
import { IndustriesPlaceholderMessage } from 'components/Placeholders';
import { InstagramDrawerContent } from './InstagramDrawerContent';
import InstagramPresetFilterChips from 'components/FilterChips/InstagramPresetFilterChips';
import NoDataSourceMarketCoverage from 'components/NoDataSourceMarketCoverage';
import NoInstagramDataSection from 'components/NoInstagramDataSection';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import Stack from 'components/Stack';
import { getInstagramDownloadOptionsAsync } from './utils';
import { healthStatFilterOptions } from 'utils/filters';
import { mixpanelTrack } from 'utils/app';
import useCountryParam from 'utils/hooks/useCountryParam';
import useDataSourceToggle from 'utils/hooks/useDataSourceToggle';

const metrics = [
    [FLAVORS_METADATA_METRIC, true],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_avg_weekly_likes, true],
    [INSTAGRAM_METADATA_BY_KEY.likes, true],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_WoW, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_WoW, true],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_MoM_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_MoM_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_QoQ_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_QoQ_rolling, true],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_YoY_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_YoY_rolling, true],
];

// Brands get a price column; precomputed because the table memoises on the
// array identity.
const brandMetrics = [
    [GOOGLE_METADATA_BY_KEY.price_segmentation, true],
    ...metrics,
];

const buildSortOptions = (forMetrics) =>
    Object.values(INSTAGRAM_SORT_OPTIONS)
        .concat(FLAVORS_SORT_OPTIONS)
        .concat([
            GOOGLE_SORT_OPTIONS.price_segmentation,
            GOOGLE_SORT_OPTIONS.price_segmentation_asc,
        ])
        .filter(({ sortBy }) => forMetrics.some(([{ key }]) => key === sortBy));

const sortOptions = buildSortOptions(metrics);
const brandSortOptions = buildSortOptions(brandMetrics);

const instagramHealthStatKeys = ['average weekly likes', 'ig posts'];

const instagramHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => instagramHealthStatKeys.includes(label),
);
// The categories tab shares this component, and a category has no price.
const instagramBrandHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => [...instagramHealthStatKeys, 'price point'].includes(label),
);

const instagramBrandCat2SubcategoryFilter = (subcategory) =>
    isInstagramBrandCat2Covered({
        cat1: subcategory.hierarchies?.[0]?.[1]?.cat1,
        cat2: subcategory.name,
    });

// TODO: move this category trends out to common file

function CategoryTrends({
    categoryQuery,
    categoryId,
    type,
    CategoryComponent,
}) {
    const { breakdown } = useParams();
    const { mutateToWithCountryParam } = useCountryParam();
    const country = useCurrentCountry();
    const trendDimension = type === 'trends' ? 'categories' : type;

    const category = categoryQuery.data?.[0];
    const name = category?.name;

    useEffect(() => {
        if (name && country) {
            const eventName =
                trendDimension === 'brands'
                    ? 'Brand Trends'
                    : 'Category Trends';

            mixpanelTrack(eventName, { name, countryName: country.name });
        }
    }, [name, country, trendDimension]);

    const hashtags = breakdown === 'hashtags' ? '*' : null;
    const brands = trendDimension === 'brands' ? '*' : null;
    const isSubcategoryTrends = trendDimension === 'categories' && !hashtags;
    const categoryDescendants =
        isSubcategoryTrends && categoryId ? [categoryId] : null;
    const categories = !isSubcategoryTrends && categoryId ? [categoryId] : null;
    const paginateBy = !!hashtags ? 'hashtags' : trendDimension;

    return (
        <CategoryComponent
            category={category}
            categoryId={categoryId}
            categoryQuery={categoryQuery}
            country={country}
            hashtags={hashtags}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            name={name}
            paginateBy={paginateBy}
        />
    );
}

function InstagramCategoryTrends({
    category,
    categoryQuery,
    categoryId,
    country,
    brands,
    categories,
    categoryDescendants,
    trendDimension,
    paginateBy,
    name,
    mixpanelMeta = { contentKey: 'Instagram' },
    filterSelectMenus = ['instagramCluster', 'healthStats'],
    defaultInitialSortOption = sortOptions.find(
        ({ sortBy }) => sortBy === metrics[1][0].key,
    ),
}) {
    return (
        <ExploreInstagramHashtagsTable
            category={category}
            categoryQuery={categoryQuery}
            categoryId={categoryId}
            country={country}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            paginateBy={paginateBy}
            name={name}
            tableName="explore-instagram"
            NameBodyColComponent={TikTokNameHashtagListCol}
            healthStatFilterOptions={
                brands
                    ? instagramBrandHealthStatFilterOptions
                    : instagramHealthStatFilterOptions
            }
            getDownloadOptions={getInstagramDownloadOptionsAsync}
            source="instagram"
            mixpanelMeta={mixpanelMeta}
            filterSelectMenus={filterSelectMenus}
            sortOptions={brands ? brandSortOptions : sortOptions}
            metrics={brands ? brandMetrics : metrics}
            defaultInitialSortOption={defaultInitialSortOption}
            PresetFilterChipsComponent={InstagramPresetFilterChips}
            DrawerComponent={Drawer}
            DrawerContentDefaultComponent={InstagramDrawerContent}
        />
    );
}

function InstagramHashtagTrends({
    category,
    categoryQuery,
    categoryId,
    country,
    hashtags,
    brands,
    categories,
    categoryDescendants,
    trendDimension,
    paginateBy,
    name,
    mixpanelMeta = { contentKey: 'Instagram' },
    filterSelectMenus = ['instagramCluster', 'healthStats'],
    defaultInitialSortOption = sortOptions.find(
        ({ sortBy }) => sortBy === metrics[1][0].key,
    ),
}) {
    return (
        <ExploreInstagramHashtagsTable
            category={category}
            categoryQuery={categoryQuery}
            categoryId={categoryId}
            country={country}
            hashtags={hashtags}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            paginateBy={paginateBy}
            name={name}
            tableName="explore-instagram"
            NameBodyColComponent={TikTokNameBodyCol}
            hashtagHealthStatFilterOptions={instagramHealthStatFilterOptions}
            healthStatFilterOptions={instagramHealthStatFilterOptions}
            getDownloadOptions={getInstagramDownloadOptionsAsync}
            source="instagram"
            mixpanelMeta={mixpanelMeta}
            filterSelectMenus={filterSelectMenus}
            sortOptions={sortOptions}
            metrics={metrics}
            defaultInitialSortOption={defaultInitialSortOption}
            PresetFilterChipsComponent={InstagramPresetFilterChips}
            DrawerComponent={Drawer}
            DrawerContentDefaultComponent={InstagramDrawerContent}
            DrawerContentHashtagComponent={InstagramDrawerContent}
        />
    );
}

function InstagramBrands({
    breakdown,
    categoryId,
    children,
    generateLocation,
    geo,
    cat1,
    location,
    mutateToWithCountryParam,
}) {
    const { modes } = useDataSourceToggle();
    // Keep the cat2 breadcrumb mounted while cat1 is unknown (transient loading
    // state during navigation). Otherwise we briefly unmount it, IndustriesOnly
    // re-mounts with `defaultOpen` and auto-pops the cat1 menu.
    const showCatTwoBreadcrumb =
        breakdown !== 'hashtags' &&
        !!categoryId &&
        (cat1 == null || hasInstagramBrandCat2Coverage(cat1));
    return (
        <Stack $space="1.5rem">
            {!showCatTwoBreadcrumb ? (
                <div
                    css={`
                        position: relative;
                        z-index: 1;
                    `}
                >
                    <DataSourceIndustriesOnlyBreadCrumbs
                        dataSource="INSTAGRAM"
                        cat1={cat1}
                        geo={geo}
                        generateLocation={generateLocation}
                        {...(categoryId && {
                            id: categoryId,
                            isActive: true,
                        })}
                    />
                </div>
            ) : (
                <div
                    css={`
                        position: relative;
                        z-index: 1;
                        max-width: calc((100% - 120px) / 2);
                    `}
                >
                    <DataSourceBreadCrumbs
                        dataSource="INSTAGRAM"
                        geo={geo}
                        cat1={cat1}
                        showIndustriesMenu={true}
                        generateLocation={generateLocation}
                        maxDepth={2}
                        replace={true}
                        skeletonSize={2}
                        subcategoryFilter={instagramBrandCat2SubcategoryFilter}
                    />
                </div>
            )}
            {breakdown === 'hashtags' ? (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    unsupportedTabs={[modes.OVERVIEW, modes.SEARCH]}
                    customTikTokTo={mutateToWithCountryParam(
                        `/explore/tiktok/hashtags/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customInstagramTo={location}
                />
            ) : (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    customOverviewTo={mutateToWithCountryParam(
                        `/explore/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customGoogleTo={mutateToWithCountryParam(
                        `/explore/google/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customTikTokTo={mutateToWithCountryParam(
                        `/explore/tiktok/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customInstagramTo={location}
                />
            )}
            {children}
        </Stack>
    );
}

function InstagramCatFives({
    breakdown,
    categoryId,
    children,
    generateLocation,
    geo,
    cat1,
    location,
    mutateToWithCountryParam,
}) {
    const { modes } = useDataSourceToggle();
    return (
        <Stack $space="1.5rem">
            <div
                css={`
                    position: relative;
                    z-index: 1;
                `}
            >
                <DataSourceBreadCrumbs
                    dataSource="INSTAGRAM"
                    geo={geo}
                    cat1={cat1}
                    showIndustriesMenu={true}
                    generateLocation={generateLocation}
                    maxDepth={4}
                    replace={true}
                    defaultOpen={breakdown !== 'hashtags'}
                />
            </div>
            {breakdown === 'hashtags' ? (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    unsupportedTabs={[modes.OVERVIEW, modes.SEARCH]}
                    customTikTokTo={mutateToWithCountryParam(
                        `/explore/tiktok/hashtags/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customInstagramTo={location}
                />
            ) : (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    customOverviewTo={mutateToWithCountryParam(
                        `/explore/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customGoogleTo={mutateToWithCountryParam(
                        `/explore/google/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customTikTokTo={mutateToWithCountryParam(
                        `/explore/tiktok/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customInstagramTo={location}
                />
            )}
            {children}
        </Stack>
    );
}

function InstagramTrends() {
    const country = useCurrentCountry();
    const { mutateToWithCountryParam } = useCountryParam();
    const location = useLocation();
    const { path } = useRouteMatch();
    const { breakdown, type, categoryId } = useParams();
    const categoryQuery = useEntities({
        enabled: !!categoryId,
        ids: [categoryId],
        entityType: 'categories',
        dataSource: DATA_SOURCES.INSTAGRAM,
    });
    const maybeCategory = categoryQuery.data?.[0];
    const generateLocation = ({ id }) => ({
        pathname: generatePath(path, { breakdown, type, categoryId: id }),
        search: location.search,
    });
    const isNotInstagramMarket =
        country && !INSTAGRAM_COUNTRIES.includes(country.text_id);
    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);
    const isBrandCat2NotCovered =
        breakdown !== 'hashtags' &&
        type === 'brands' &&
        maybeCategory?.depth === 'cat2' &&
        !isInstagramBrandCat2Covered({
            cat1: maybeCategory.hierarchies?.[0]?.[1]?.cat1,
            cat2: maybeCategory.name,
        });
    const DimensionComponent =
        type === 'trends' ? InstagramCatFives : InstagramBrands;
    const CategoryComponent =
        breakdown === 'hashtags'
            ? InstagramHashtagTrends
            : InstagramCategoryTrends;
    const industryName = maybeCategory?.hierarchies?.[0]?.[1]?.cat1;

    return (
        <PagePadding className="max-w-screen-2xl">
            <PageBar />
            <Stack $space="1.25rem">
                <DimensionComponent
                    breakdown={breakdown}
                    categoryId={categoryId}
                    generateLocation={generateLocation}
                    isNotInstagramMarket={isNotInstagramMarket}
                    location={location}
                    mutateToWithCountryParam={mutateToWithCountryParam}
                    geo={country?.text_id}
                    cat1={industryName}
                >
                    {isNotInstagramMarket ? (
                        <NoInstagramDataSection />
                    ) : industryName &&
                      country &&
                      !isCovered({
                          geo: country?.text_id,
                          dataSource: DATA_SOURCES.INSTAGRAM,
                          cat1: industryName,
                      }) ? (
                        <NoDataSourceMarketCoverage
                            dataSource={DATA_SOURCES.INSTAGRAM}
                        />
                    ) : isBrandCat2NotCovered ? (
                        <NoDataSourceMarketCoverage
                            dataSource={DATA_SOURCES.INSTAGRAM}
                        />
                    ) : !categoryId && breakdown !== 'hashtags' ? (
                        <IndustriesPlaceholderMessage />
                    ) : (
                        <CategoryTrends
                            categoryQuery={categoryQuery}
                            categoryId={categoryId}
                            type={type}
                            CategoryComponent={CategoryComponent}
                        />
                    )}
                </DimensionComponent>
            </Stack>
        </PagePadding>
    );
}

export default InstagramTrends;
